import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert } from "reactstrap";
import {useGetPublicJobQuery} from "../../store/services/publicApi";
import {CurrencyValue, DateValue, MiniInfoCard} from "../../components/Public/Shared";
import {prettyEnum} from "../../components/Public/utils";
import {getPlayStoreUrl} from "../../utils";

const PLAY_STORE_URL = getPlayStoreUrl();

const JobDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: job, isLoading, error } = useGetPublicJobQuery(id || "");

    if (isLoading) {
        return (
            <div className="text-center py-9 mt-9">
                <Spinner color="primary" />
                <p className="mt-3 text-muted">Loading job details...</p>
            </div>
        );
    }

    if (error || !job) {
        return (
            <Container className="py-9 mt-9 text-center">
                <Alert color="danger">
                    {error ? "Error loading job details. Please try again." : "Job not found."}
                </Alert>
                <Button color="primary" onClick={() => navigate("/jobs")}>Back to Jobs</Button>
            </Container>
        );
    }

    const company = job.company;
    const location = job.location;

    return (
        <div className="public-job-detail-page">
            <section className="oec-jobs-hero">
                <div className="oec-section-inner">
                    <button type="button" className="oec-jobs-back" onClick={() => navigate("/jobs")}>
                        <i className="fas fa-arrow-left"/> All jobs
                    </button>
                    <h1>{job.title}</h1>
                    <p className="oec-job-detail-facts">
                        {company?.name && <span><i className="fas fa-industry"/>{company.name}</span>}
                        <span><i className="fas fa-map-marker-alt"/>{location || "Remote"}</span>
                        {job.employmentType && <span><i className="far fa-clock"/>{prettyEnum(job.employmentType)}</span>}
                    </p>
                    <button
                        type="button"
                        className="oec-search-btn oec-job-apply"
                        onClick={() => window.open(PLAY_STORE_URL, "_blank", "noopener,noreferrer")}
                    >
                        Apply on mobile
                    </button>
                </div>
            </section>

            <section className="oec-section">
                <div className="oec-section-inner">
                    <Row>
                        <Col lg="8">
                            <Card className="border-0 shadow-lg mb-4">
                                <CardBody className="p-5">
                                    <h4 className="h4 font-weight-bold mb-4 border-bottom pb-3 text-primary">Job Description</h4>
                                    <div className="job-description-content text-dark lead" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>
                                        {job.description}
                                    </div>

                                    <h4 className="h4 font-weight-bold mt-5 mb-4 border-bottom pb-3 text-primary">Key Details</h4>
                                    <Row>
                                        <Col md="6" className="mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="icon icon--sm icon-shape icon-shape-primary rounded-circle mr-3 shadow-sm">
                                                    <i className="ni ni-briefcase-24" />
                                                </div>
                                                <div>
                                                    <small className="text-muted d-block">Employment Type</small>
                                                    <span className="font-weight-bold h6 mb-0 text-dark">{prettyEnum(job.employmentType)}</span>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md="6" className="mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="icon icon--sm icon-shape icon-shape-primary rounded-circle mr-3 shadow-sm">
                                                    <i className="ni ni-money-coins" />
                                                </div>
                                                <div>
                                                    <small className="text-muted d-block">Salary Range</small>
                                                    <span className="font-weight-bold h6 mb-0 text-dark">
                                                        <CurrencyValue value={job.salary} />
                                                    </span>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md="6" className="mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="icon icon--sm icon-shape icon-shape-primary rounded-circle mr-3 shadow-sm">
                                                    <i className="ni ni-hat-3" />
                                                </div>
                                                <div>
                                                    <small className="text-muted d-block">Seniority Level</small>
                                                    <span className="font-weight-bold h6 mb-0 text-dark">{prettyEnum(job.seniorityLevel)}</span>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md="6" className="mb-3">
                                            <div className="d-flex align-items-center">
                                                <div className="icon icon--sm icon-shape icon-shape-primary rounded-circle mr-3 shadow-sm">
                                                    <i className="ni ni-calendar-grid-58" />
                                                </div>
                                                <div>
                                                    <small className="text-muted d-block">Deadline</small>
                                                    <span className="font-weight-bold h6 mb-0 text-dark">
                                                        <DateValue value={job.deadline} />
                                                    </span>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg="4">
                            <Card className="border-0 shadow-lg position-sticky" style={{ top: '100px' }}>
                                <CardBody className="text-center p-4">
                                    <div className="mx-auto bg-white rounded-circle shadow-sm mb-4 d-flex align-items-center justify-content-center border" style={{ width: '100px', height: '100px', overflow: 'hidden' }}>
                                        <img
                                            src={company?.logo}
                                            alt={company?.name}
                                            className="w-100 h-100"
                                            style={{ objectFit: 'contain', padding: '10px' }}
                                        />
                                    </div>
                                    <h5 className="h4 font-weight-bold mb-2 text-dark">{company?.name}</h5>
                                    <p className="text-muted text-sm line-clamp-3 mb-4">
                                        {company?.description || "--"}
                                    </p>
                                    <Button color="primary" block outline className="rounded-pill py-2 shadow-sm font-weight-bold" onClick={() => navigate(`/company/${company?.id}`)}>
                                        View Company Profile
                                    </Button>
                                </CardBody>
                            </Card>

                            <MiniInfoCard
                                title={"Questions?"}
                                subtitle={"Need more information about this role or the application process?"}
                                action={{
                                    label: "Contact Us",
                                    destination: "/about#contact"
                                }}
                            />
                        </Col>
                    </Row>
                </div>
            </section>
        </div>
    );
};

export default JobDetail;
